import { useRef, useState } from "react";
import { Mic, MicOff, Pause, Play, Square, Volume2, VolumeX, Send, Loader2, Sparkles } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Progress } from "../ui/progress";
import { useVoiceCapture } from "./useVoiceCapture";

const MUTE_KEY = "dp_voice_muted";

export const VoiceTag = ({ mark }) => (mark ? <span className={`ml-1.5 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${mark === "review" ? "bg-amber-100 text-amber-800" : mark === "manual" ? "bg-slate-200 text-slate-700" : "bg-sky-100 text-sky-800"}`} data-testid="voice-tag" data-mark={mark}>{mark === "review" ? "revisar" : mark === "manual" ? "editado" : "voz"}</span> : null);

// Shared voice layer: transcription -> structured interpretation (server-validated) -> onApply into the existing form state.
export function VoiceAssistant({ form, step, fields, values, options, onApply, onAction, token, stepLabel }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);
  const [typed, setTyped] = useState("");
  const [err, setErr] = useState("");
  const [muted, setMuted] = useState(() => localStorage.getItem(MUTE_KEY) === "1");
  const audio = useRef(null);
  const hdr = token ? { headers: { "X-Admission-Token": token } } : {};
  const say = async (text) => {
    if (muted || !text) return;
    try {
      cap.pause();
      const { data } = await api.post("/voice/speak", { text }, { ...hdr, responseType: "blob" });
      const a = new Audio(URL.createObjectURL(data)); audio.current = a;
      await new Promise((ok) => { a.onended = ok; a.onerror = ok; a.play().catch(ok); });
    } catch { /* voice is optional */ } finally { if (cap.state !== "off") cap.resume(); }
  };
  const handle = async (text) => {
    const t = text.trim(); if (!t) return;
    const hist = log.slice(-6);
    setLog((l) => [...l, { role: "user", text: t }]); setBusy(true); setErr("");
    try {
      const { data } = await api.post("/voice/interpret", { form, step: step != null ? String(step) : null, text: t, values, options, history: hist }, hdr);
      onApply(data);
      setLog((l) => [...l, { role: "assistant", text: data.reply }]);
      if (data.action === "pause") cap.pause(); else if (data.action !== "none") onAction?.(data.action);
      await say(data.reply);
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  const segment = async (blob) => {
    if (blob.size < 2500) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("audio", blob, blob.type.includes("mp4") ? "fala.m4a" : "fala.webm");
      const { data } = await api.post("/voice/transcribe", fd, hdr);
      cap.setInterim("");
      if (data.text) await handle(data.text); else setBusy(false);
    } catch (e) { setErr(`${errMsg(e)} Você pode digitar abaixo.`); setBusy(false); }
  };
  const cap = useVoiceCapture(segment);
  const startMic = async () => {
    setErr("");
    try {
      await cap.start();
      if (!log.length) { const hi = "Olá! Pode falar os dados naturalmente, vários de uma vez. Eu preencho e pergunto o que faltar."; setLog([{ role: "assistant", text: hi }]); say(hi); }
    } catch (e) {
      setErr(e?.name === "NotAllowedError" ? "Permissão do microfone negada. Libere o microfone no navegador ou digite abaixo." : "Microfone indisponível neste navegador. Digite abaixo para preencher.");
    }
  };
  const toggleMute = () => { const m = !muted; setMuted(m); localStorage.setItem(MUTE_KEY, m ? "1" : "0"); if (m) audio.current?.pause(); };
  const req = fields.filter((f) => f.required);
  const done = req.filter((f) => values?.[f.k]).length;
  const pct = req.length ? Math.round((done / req.length) * 100) : 100;
  const pending = req.filter((f) => !values?.[f.k]);
  if (!open) return <Button type="button" variant="outline" className="border-sky-300 text-sky-800 hover:bg-sky-50" onClick={() => setOpen(true)} data-testid="voice-open"><Mic className="h-4 w-4 mr-1.5" />Preencher por voz</Button>;
  const live = cap.state === "listening";
  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50/50 p-3 space-y-2.5" data-testid="voice-panel">
      <div className="flex flex-wrap items-center gap-2">
        <Sparkles className="h-4 w-4 text-sky-700" /><span className="text-sm font-semibold text-sky-900">Assistente de voz DPdigi</span>
        {live && <span className="flex items-center gap-1.5 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-bold text-rose-700" data-testid="voice-recording"><span className="h-2 w-2 rounded-full bg-rose-600 animate-pulse" />GRAVANDO</span>}
        {cap.state === "paused" && <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-bold text-slate-700" data-testid="voice-paused">PAUSADO</span>}
        {busy && <span className="flex items-center gap-1 text-[11px] text-slate-600"><Loader2 className="h-3 w-3 animate-spin" />processando…</span>}
        <span className="ml-auto flex gap-1">
          {cap.state === "off" ? <Button type="button" size="sm" onClick={startMic} data-testid="voice-start"><Mic className="h-3.5 w-3.5 mr-1" />Ativar microfone</Button>
            : <>{live ? <Button type="button" size="sm" variant="outline" onClick={cap.pause} data-testid="voice-pause"><Pause className="h-3.5 w-3.5" /></Button>
              : <Button type="button" size="sm" variant="outline" onClick={cap.resume} data-testid="voice-resume"><Play className="h-3.5 w-3.5" /></Button>}
              <Button type="button" size="sm" variant="outline" onClick={cap.stop} data-testid="voice-stop"><Square className="h-3.5 w-3.5" /></Button></>}
          <Button type="button" size="sm" variant="ghost" onClick={toggleMute} title={muted ? "Ativar voz" : "Silenciar voz"} data-testid="voice-mute">{muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}</Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => { cap.stop(); setOpen(false); }} title="Desativar assistente" data-testid="voice-close"><MicOff className="h-4 w-4" /></Button>
        </span>
      </div>
      {live && <div className="h-1 rounded bg-sky-100 overflow-hidden"><div className="h-full bg-sky-500 transition-[width] duration-100" style={{ width: `${cap.level * 100}%` }} /></div>}
      <div className="flex items-center gap-2 text-[11px] text-slate-600"><span data-testid="voice-step">{stepLabel}</span><Progress value={pct} className="h-1.5 flex-1" /><span data-testid="voice-progress">{pct}%</span></div>
      {pending.length > 0 && <div className="text-[11px] text-slate-500" data-testid="voice-pending">Faltam: {pending.map((f) => f.label).join(", ")}</div>}
      <div className="max-h-44 overflow-y-auto space-y-1.5" data-testid="voice-log">
        {log.map((m, i) => <div key={i} className={`rounded-lg px-2.5 py-1.5 text-sm ${m.role === "user" ? "bg-white border ml-8" : "bg-sky-100 text-sky-950 mr-8"}`}>{m.text}</div>)}
        {cap.interim && <div className="ml-8 rounded-lg border border-dashed bg-white/70 px-2.5 py-1.5 text-sm italic text-slate-500" data-testid="voice-interim">{cap.interim}</div>}
      </div>
      {err && <div className="text-xs text-rose-700" role="alert" data-testid="voice-error">{err}</div>}
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); handle(typed); setTyped(""); }}>
        <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Ou digite: ex. corrija o salário para 3.500" className="h-9 bg-white" data-testid="voice-text" />
        <Button type="submit" size="sm" disabled={busy || !typed.trim()} data-testid="voice-send"><Send className="h-3.5 w-3.5" /></Button>
      </form>
      <p className="text-[10px] leading-snug text-slate-400">O áudio é transcrito pela OpenAI (via Emergent) e não é armazenado. Nada é enviado à DPdigi sem a sua confirmação no botão final.</p>
    </div>);
}
